import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import { Button } from "react-bootstrap";
import TopHeader from "../Component/TopHeader";
import UserBookingsFilters from "../Component/Filters/UserBookingsFilters";
import KebabMenu from "../Component/KebabMenu";
import FilterPopover from "../Component/FilterPopover";
import StatusDot from "../Component/StatusDot";
import Pagination from "../Component/Pagination";
import ConfirmModal from "../Component/Models/ConfirmModal";

import { toggleRightSidebarAction } from "../../Store/Actions/AuthActions";

import {
  canCancelBooking,
  display,
  formatBookingArrival,
  showToast,
} from "../../utils/helper";
import { trackEvent } from "../../utils/analytics";
import { API } from "../../utils/api";
import usePaymentRedirectReturn from "../../utils/usePaymentRedirectReturn";
import {
  deleteUserBookingAction,
  getUserBookingsListAction,
} from "../../Store/Actions/BookingActions";
import { updateUserBookingItem } from "../../Store/Reducers/BookingReducer";
import DetailsRightSidebar from "../Component/DetailsRightSidebar";
import UserSideBarMenu from "../Component/UserSidebarMenu";

const BookingsList = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const deepLinkBookingId = searchParams.get("bookingId");
  const menuState = useSelector((state) => state.auth.isMenuOpen);
  // const rightSidebar = useSelector((state) => state.auth.isRightSidebarOpen);
  const { myBookings } = useSelector((state) => state.bookings);

  const [loading, setLoading] = useState(false);
  const [delLoading, setDelLoading] = useState({});
  const [filters, setFilters] = useState({});
  const [deepLinkLoading, setDeepLinkLoading] = useState(false);
  const openedDeepLinkRef = useRef(null);

  const stripEmpty = (obj) =>
    Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v !== "" && v !== null && v !== undefined),
    );
  const [showSidebar, toggleShowRightSidebar] = useState({
    show: false,
    item: {},
  });
  const [confirmDel, setConfirmDel] = useState({ show: false, item: null });
  const [pagging, setPagging] = useState({
    totalItems: "",
    itemsPerPage: "",
    currentPage: "",
    totalPages: "",
    nextPage: 1,
    limit: 15,
  });

  const askDeleteBooking = (item) => {
    if (delLoading[item.id]) return;
    setConfirmDel({ show: true, item });
  };

  const performDeleteBooking = () => {
    const item = confirmDel.item;
    if (!item || delLoading[item.id]) return;

    setDelLoading({ ...delLoading, [item.id]: true });

    deleteUserBookingAction({ id: item.id }, dispatch)
      .then((res) => {
        setDelLoading({ ...delLoading, [item.id]: false });
        setConfirmDel({ show: false, item: null });
        const type = res && res.type === "success" ? "success" : "error";
        if (type === "success") {
          trackEvent("web_booking_cancelled", {
            booking_id: item.id,
            role: "user",
          });
        }
        showToast(type, (res && res.message) || "Booking cancelled");
      })
      .catch((err) => {
        setDelLoading({ ...delLoading, [item.id]: false });
        setConfirmDel({ show: false, item: null });
        showToast("error", (err && err.message) || "Could not cancel booking");
      });
  };

  const getBookingsList = (page = 1, overrideFilters) => {
    if (!loading) {
      setLoading(true);

      const activeFilters = stripEmpty(overrideFilters || filters);
      const listParams = {
        page,
        limit: pagging.limit,
        status: "accepted,request-pending,completed,rejected,cancelled",
        ...activeFilters,
      };

      getUserBookingsListAction(listParams, dispatch)
        .then((res) => {
          setLoading(false);
          if (res.type === "success") {
            setPagging({
              ...pagging,
              ...res.pagination,
            });
          }
        })
        .catch((err) => {
          setLoading(false);

          showToast("error", err.message);
        });
    }
  };

  const renderPagging = () => (
    <Pagination
      currentPage={pagging.currentPage || 1}
      totalPages={pagging.totalPages || 1}
      totalItems={pagging.totalItems}
      onPageChange={(p) => getBookingsList(p)}
    />
  );

  useEffect(() => {
    if (myBookings.length === 0) {
      getBookingsList();
    }
  }, []);

  usePaymentRedirectReturn(() => getBookingsList(1));

  useEffect(() => {
    if (!deepLinkBookingId) return;
    if (openedDeepLinkRef.current === deepLinkBookingId) return;

    const fromList = myBookings.find((b) => b.id === deepLinkBookingId);
    if (fromList) {
      openedDeepLinkRef.current = deepLinkBookingId;
      toggleShowRightSidebar({ show: true, item: fromList });
      return;
    }

    if (loading || deepLinkLoading) return;

    setDeepLinkLoading(true);
    API.getSingleBooking(deepLinkBookingId)
      .then((res) => {
        if (res && res.type === "success" && res.data) {
          openedDeepLinkRef.current = deepLinkBookingId;
          toggleShowRightSidebar({ show: true, item: res.data });
        } else {
          showToast("error", (res && res.message) || "Booking not found");
        }
      })
      .catch((err) =>
        showToast("error", (err && err.message) || "Could not open booking"),
      )
      .finally(() => setDeepLinkLoading(false));
  }, [deepLinkBookingId, myBookings, loading]);

  return (
    <>
      <div className={`app-wrapper ${menuState ? "open" : ""}`}>
        <UserSideBarMenu />

        <div className="content-wrapper">
          <TopHeader title={"Bookings"} />

          <div className="content">
            <div className="list-toolbar">
              <div className="list-toolbar-spacer" />

              <FilterPopover
                count={
                  Object.values(filters).filter(
                    (v) => v !== "" && v !== null && v !== undefined,
                  ).length
                }
              >
                {(close) => (
                  <UserBookingsFilters
                    initial={filters}
                    onApply={(values) => {
                      setFilters(values);
                      getBookingsList(1, values);
                      close();
                    }}
                  />
                )}
              </FilterPopover>
            </div>

                <div className="data-listing">
                  <table className="table">
                    <thead className="thead-light">
                      <tr>
                        <th width="13%">Name</th>
                        <th width="22%">Address</th>
                        <th width="13%">Arrival</th>
                        <th width="9%" className="text-center">
                          Amount
                        </th>
                        <th width="11%">Status</th>
                        <th width="6%" className="text-center">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {myBookings.length > 0 &&
                        myBookings.map((item) => (
                          <tr key={`bk-${item.id}`}>
                            <td>{display(item.spot && item.spot.name)}</td>
                            <td>{display(item.spot && item.spot.address)}</td>
                            <td>{formatBookingArrival(item)}</td>
                            <td className="text-center">${display(item.grossAmount, "0")}</td>
                            <td>
                              <StatusDot status={item.status} />
                            </td>
                            <td className="text-center">
                              <KebabMenu
                                items={[
                                  {
                                    label: "View",
                                    icon: "fas fa-eye",
                                    onClick: () => {
                                      toggleShowRightSidebar({
                                        show: true,
                                        item,
                                      });
                                      toggleRightSidebarAction(true, dispatch);
                                    },
                                  },
                                  ...(canCancelBooking(item)
                                    ? [
                                        {
                                          label: delLoading[item.id]
                                            ? "Cancelling…"
                                            : "Cancel booking",
                                          icon: "fas fa-trash",
                                          danger: true,
                                          disabled: !!delLoading[item.id],
                                          onClick: () => askDeleteBooking(item),
                                        },
                                      ]
                                    : []),
                                ]}
                              />
                            </td>
                          </tr>
                        ))}

                      {myBookings.length === 0 && (
                        <tr>
                          <td colSpan="6">
                            <p className="text-center">
                              No Bookings Available.
                            </p>
                          </td>
                        </tr>
                      )}

                      {loading && (
                        <tr>
                          <td colSpan="6">
                            <p className="text-center">
                              <i className="fas fa-spinner fa-spin" />
                            </p>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {renderPagging()}
          </div>
        </div>

        {showSidebar.show && (
          <DetailsRightSidebar
            type="booking"
            item={showSidebar.item}
            closeSidebar={() => {
              toggleShowRightSidebar({ show: false, item: {} });
            }}
            onUpdated={(updated) => {
              if (updated && updated.id) {
                dispatch(updateUserBookingItem({ item: updated }));
              }
            }}
          />
        )}

        <ConfirmModal
          show={confirmDel.show}
          title="Cancel Booking"
          message={
            confirmDel.item && confirmDel.item.spot && confirmDel.item.spot.name
              ? `Cancel booking at "${confirmDel.item.spot.name}"?`
              : "Are you sure want to cancel this booking?"
          }
          confirmLabel="Yes, cancel"
          cancelLabel="No"
          busy={confirmDel.item ? !!delLoading[confirmDel.item.id] : false}
          onClose={() => setConfirmDel({ show: false, item: null })}
          onConfirm={performDeleteBooking}
        />
      </div>
    </>
  );
};

export default BookingsList;
